<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { useSystemStore } from '@/stores/system'
import { useFavoriteStore } from '@/stores/favorite'
import { ElMessage } from 'element-plus'
import { recordVisit } from '@/api/promotion'

const cartStore = useCartStore()
const userStore = useUserStore()
const systemStore = useSystemStore()
const favoriteStore = useFavoriteStore()

onMounted(() => {
  // 初始化应用
  initApp()

  // 监听网络状态
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  // 监听页面可见性
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

const initApp = async () => {
  // 初始化购物车
  cartStore.loadCart()

  // 初始化用户信息
  if (userStore.token && userStore.userInfo) {
    userStore.init()
    if (userStore.isLoggedIn) {
      favoriteStore.loadFavorites()
    }
  }

  // 加载系统配置
  systemStore.init()

  // 检测推广分享链接访问：仅未登录用户才有效
  const params = new URLSearchParams(window.location.search)
  const shareCode = params.get('ref')
  if (shareCode && !userStore.isLoggedIn) {
    // 持久化 shareCode，注册/首次登录时使用
    sessionStorage.setItem('promotion_share_code', shareCode)
    // 上报访问记录
    try {
      await recordVisit({
        shareCode,
        visitorDevice: navigator.userAgent,
        referrerUrl: document.referrer || undefined
      })
    } catch (e) {
      // 静默失败，不影响正常访问
    }
  }
}

const handleOnline = () => {
  ElMessage.success('网络连接已恢复')
}

const handleOffline = () => {
  ElMessage.warning('网络连接已断开')
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    // 页面不可见
    console.log('页面隐藏')
  } else {
    // 页面可见
    console.log('页面显示')
    // 可以在这里刷新数据
  }
}
</script>

<style lang="scss">
// 全局样式重置
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html, body {
  height: 100%;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  height: 100%;
  color: #2c3e50;
}

// 滚动条样式
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;

  &:hover {
    background: #a8a8a8;
  }
}

// 通用工具类
.clearfix::after {
  content: "";
  display: table;
  clear: both;
}

.text-center {
  text-align: center;
}

.text-left {
  text-align: left;
}

.text-right {
  text-align: right;
}

.pull-left {
  float: left;
}

.pull-right {
  float: right;
}

// 响应式辅助类
.hidden-xs {
  @media (max-width: 767px) {
    display: none !important;
  }
}

.hidden-sm {
  @media (min-width: 768px) and (max-width: 991px) {
    display: none !important;
  }
}

.hidden-md {
  @media (min-width: 992px) and (max-width: 1199px) {
    display: none !important;
  }
}

.hidden-lg {
  @media (min-width: 1200px) {
    display: none !important;
  }
}

// 动画类
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
